import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HideImageTwoToneIcon from "@mui/icons-material/HideImageTwoTone";
import { Box, Card, CardActionArea, Icon, Stack, Typography, useMediaQuery } from "@mui/material";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import type React from "react";
import { Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import {
	PriceType,
	type GalleryItemDto,
	type IFrameAssetsAdvertDto,
} from "../../../../api/rest/generated/aPIForPublicPartOfApplicationInzerceNemovitéhoMajetku.schemas.ts";
import {
	useGetApiAssetsmultimediaAdvertisementIdGallerySuspense,
	useGetApiAssetsmultimediaThumbnailIdSuspense,
} from "../../../../api/rest/generated/assets-multimedia/assets-multimedia.ts";
import { iframeDetailRedirect } from "../../../../utills/iframeDetailRedirect.ts";
import SzBackdrop from "../../UISharedConstructors/Loading/SzBackDrop.tsx";
import AdvertisementDate from "./AdvertisementData.tsx";

const formatCzechPrice = (price: number) => price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");

export interface AdDetailPreviewProps {
	data: IFrameAssetsAdvertDto;
}

const clientBaseURL = import.meta.env.VITE_API;

const Image: React.FC<{ image: GalleryItemDto | undefined; }> = ({ image }) => {
	const imageUrl = `${clientBaseURL}${image?.imageUrl?.replace("/image/","/thumbnail/") ?? "undefined-image-url"}`;

	if (!image) {
		return (
			<Typography textAlign="center" fontSize="6rem">
				<HideImageTwoToneIcon fontSize="inherit" color="disabled" />
			</Typography>
		);
	}

	return (
		<img
			src={imageUrl}
			alt={`Alternativní popis obrázku: ${image.description ?? "nevyplněno"}`}
			style={{
				width: "100%",
				height: "100%",
				objectFit: "cover",
			}}
		/>
	);
};

const Thumbnail: React.FC<{ adID: number; }> = ({ adID }) => {
	const imageQuery = useGetApiAssetsmultimediaAdvertisementIdGallerySuspense(adID, {
		query: {
			retry: false,
		},
	});

	const data = imageQuery.data[0];

	return <Image image={data} />;
};

const AdvertisementItem: React.FC<AdDetailPreviewProps> = ({ data }) => {
	const [isHovered, setIsHovered] = useState(false);

	const isSmall = useMediaQuery("(max-width:600px)");

	const handleRedirect = () => {
		iframeDetailRedirect(data.id);
	};

	const priceLabelType = data.priceType === PriceType.AskingPrice ? "Vyvolávací cena" : "Cena";

	return (
		<Card
			sx={{
				boxSizing: "border-box",
				marginY: "1.5em",
				display: "flex",
				flexDirection: "row",
				"@container main-layout (max-width: 400px)": {
					flexDirection: "column",
				},
			}}
		>
			<CardActionArea
				onClick={handleRedirect}
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				sx={{
					display: "flex",
					flexDirection: "row",
					"@container main-layout (max-width: 400px)": {
						flexDirection: "column",
					},
					width: "100%",
					height: "100%",
					backgroundColor: "grey.100",
					padding: 0,
					margin: 0,
					boxSizing: "border-box",
					gap: "1rem",
				}}
			>
				<Box
					sx={{
						position: "relative",
						display: "flex",
						minWidth: "3rem",
						maxWidth: "15rem",
						minHeight: isSmall ? "10em" : undefined,
						"@container main-layout (max-width: 400px)": {
							maxWidth: "20rem",
						},
						width: "100%",
						height: "100%",
						justifyContent: "center",
						alignItems: "center",
						backgroundColor: "grey.300",
					}}
				>
					<Suspense fallback={<SzBackdrop sizeVariant={4} />}>
						<QueryErrorResetBoundary>
							{() => (
								<ErrorBoundary
									fallbackRender={() => (
										<Typography
											sx={{
												minHeight: "10rem",
												"@container main-layout (max-width: 400px)": {
													minHeight: "10rem",
												},
											}}
											minHeight="1.3em"
											display="flex"
											alignItems="center"
											justifyContent="center"
											textAlign="center"
											fontSize="6rem"
										>
											<HideImageTwoToneIcon fontSize="inherit" color="disabled" />
										</Typography>
									)}
								>
									<Box display="flex">
										<Thumbnail adID={data.id} />
									</Box>
								</ErrorBoundary>
							)}
						</QueryErrorResetBoundary>
					</Suspense>
				</Box>
				<Stack
					direction="column"
					width="100%"
					height="inherit"
					boxSizing="border-box"
					paddingBottom="1em"
					justifyContent="space-between"
				>
					<Box width="100%" height="auto">
						<Typography
							fontSize="1rem"
							sx={{ color: "secondary.main" }}
							fontWeight="bolder"
							variant="h6"
							component="h3"
						>
							{data.name}
						</Typography>
						{data?.showPrice && (
							<Typography
								fontSize="0.8rem"
								fontWeight="bolder"
								sx={{ color: "info.main" }}
								variant="subtitle1"
								component="div"
							>
								{`${priceLabelType}: ${formatCzechPrice(data.price as number)} Kč`}
							</Typography>
						)}
					</Box>
					<Stack
						sx={{
							flexDirection: "row",
							gap: 3,
							"@container main-layout (max-width: 400px)": {
								flexDirection: "column",
								gap: 1,
							},
						}}
						flexWrap="wrap"
					>
						<AdvertisementDate variant="toDate" date={data.publishTo as string} />
					</Stack>
				</Stack>
				<Icon
					sx={{
						display: "inline-flex",
						justifyContent: "center",
						alignItems: "center",
						height: "2rem",
						width: "2rem",
						borderRadius: "3px",
						color: data.id % 2 === 0 ? "info.main" : "primary.main",
						border: "1px solid",
						backgroundColor: "white",
						alignSelf: "flex-end",
						boxSizing: "border-box",
						marginRight: "1rem",
						marginBottom: "1rem",
						...(isHovered && {
							borderRadius: "28%",
							backgroundColor: data.id % 2 === 0 ? "info.main" : "primary.main",
							color: "white",
							border: "none",
						}),
					}}
				>
					<ArrowForwardIcon />
				</Icon>
			</CardActionArea>
		</Card>
	);
};

export default AdvertisementItem;
